'use client';

import React, { useCallback, useEffect, useState } from 'react';
import salsify from 'salsify-experiences-sdk';
import Script from 'next/script';
import { Heading, Button } from '@petsmart-ui/sparky';
import { clsx } from '@libs/common-next';
import type { BrandContentProps } from './types';

import './BrandContent.scss';

const SALSIFY_ORIGIN = 'salsify';
const WEB_COLLAGE_SITECODE = 'petsmart';
const WEB_COLLAGE_CHECK_TIMEOUT = 2 * 1000;
const WEB_COLLAGE_SCRIPT_SRC =
    '//scontent.webcollage.net/api/v2/product-content';
const WEB_COLLAGE_CONTENT_SETTINGS = {
    'power-page': {
        autoPlayAndStop: true,
        reload: true,
    },
};
const WEB_COLLAGE_SUBDOM_SELECTOR = 'syndigo-powerpage'; // HTML tag that Webcollage uses to render the content

export default function BrandContent({
    salsifyClientId,
    salsifyIdType,
    productId,
}: Readonly<BrandContentProps>) {
    const [showAll, setShowAll] = useState(false);
    const [webCollageFound, setWebCollageFound] = useState(true);
    const [salsifyFound, setSalsifyFound] = useState(true);

    // Initialize Salsify content

    const initializeSalsify = useCallback(() => {
        const checkProduct = async () => {
            const exists = await salsify.enhancedContent.exists(productId);

            if (exists) {
                const salsifyTargetDiv = document.querySelector(
                    '#salsify-target'
                ) as HTMLDivElement;

                if (salsifyTargetDiv) {
                    salsify.enhancedContent.renderIframe(
                        salsifyTargetDiv,
                        productId
                    );
                }
            }

            setSalsifyFound(Boolean(exists));
        };

        if (salsifyClientId && salsifyIdType) {
            salsify.init({
                clientId: salsifyClientId,
                enhancedContent: { idType: salsifyIdType },
            });

            // Check if enhanced content exists for product
            checkProduct();

            // Listen for messages from Salsify to adjust iframe to fit content
            window.addEventListener('message', event => {
                if (event.origin.includes(SALSIFY_ORIGIN)) {
                    const salsifyEcIframe: HTMLIFrameElement =
                        document.getElementById(
                            'salsify-ec-iframe'
                        ) as HTMLIFrameElement;

                    if (salsifyEcIframe) {
                        salsifyEcIframe.height = event.data.height as string;
                        salsifyEcIframe.style.height = `${event.data.height}px`;
                    }
                }
            });
        }
    }, [productId, salsifyClientId, salsifyIdType]);

    // Effect to handle initial setup and re-initialization if IDs change
    useEffect(() => {
        if (!window.flags?.IS_PERFORMANCE_MODE && !salsify.initialized) {
            initializeSalsify();
        }
    }, [initializeSalsify, salsifyClientId, salsifyIdType]);

    // Initialize Webcollage content
    const initializeWebcollage = () => {
        if (window.flags?.IS_PERFORMANCE_MODE) {
            return;
        }

        if (window.Webcollage) {
            window.Webcollage.loadProductContent(
                WEB_COLLAGE_SITECODE,
                String(productId),
                WEB_COLLAGE_CONTENT_SETTINGS
            );

            setTimeout(() => {
                const webCollageDiv = document.querySelector(
                    WEB_COLLAGE_SUBDOM_SELECTOR
                ) as HTMLDivElement;

                setWebCollageFound(Boolean(webCollageDiv));
            }, WEB_COLLAGE_CHECK_TIMEOUT);
        }
    };

    // Classes
    const brandClasses = clsx(
        'sparky-l-container',
        'brand-content-wrapper',
        !salsifyFound && !webCollageFound && `hide`
    );
    const showMoreClasses = clsx('show-more-container', showAll && `open`);

    // Display logic
    const showText = showAll ? 'Show less' : 'Show more';

    return (
        <>
            <Script
                id="webcollage-script"
                src={WEB_COLLAGE_SCRIPT_SRC}
                strategy="afterInteractive"
                onLoad={initializeWebcollage}
            />
            <div className={brandClasses} data-testid="brand-content">
                <Heading tagName="h2" size="title">
                    From the manufacturer
                </Heading>
                <div className={showMoreClasses}>
                    {salsifyFound && <div id="salsify-target" />}
                    {webCollageFound && <div id="wc-power-page" />}
                </div>
                <Button
                    onClick={() => {
                        setShowAll(!showAll);
                        return false;
                    }}
                    text={showText}
                />
            </div>
        </>
    );
}
